import { applyTheme } from './themeStorage';
import type { Theme } from './types';

/*
The page crossfades between themes through a view transition: the browser snapshots the page,
applies the new theme underneath and fades between the two in one composited layer, charts
included. Browsers without view transitions, and users who ask for reduced motion, get the
switch at once. Durations live in styles/base/_theme-transition.scss.
*/

let wanted: Theme | null = null;
let crossfading = false;
let switchingFrame = 0;

function prefersReducedMotion(): boolean {
    return (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
}

/**
 * Applies the theme with component transitions held off for two frames, so every colour
 * changes in the same frame instead of some components fading at their own pace.
 */
function applyAtOnce(theme: Theme): void {
    const root = document.documentElement;
    const frame = ++switchingFrame;
    root.dataset.themeSwitching = '';
    applyTheme(theme);
    requestAnimationFrame(() =>
        requestAnimationFrame(() => {
            if (frame === switchingFrame) delete root.dataset.themeSwitching;
        })
    );
}

/**
 * While a view transition runs, the browser sends every click to <html> instead of the element
 * under the pointer, so a quick second click on the toggle would be lost. This hands the ones
 * that land on the toggle to it.
 */
function forwardToggleClick(event: MouseEvent): void {
    if (event.target !== document.documentElement) return;
    const toggle = document.querySelector<HTMLElement>('[data-theme-toggle]');
    if (!toggle) return;
    const box = toggle.getBoundingClientRect();
    const { clientX: x, clientY: y } = event;
    if (x >= box.left && x <= box.right && y >= box.top && y <= box.bottom) toggle.click();
}

function switchToWanted(): void {
    const theme = wanted;
    if (theme === null || document.documentElement.dataset.theme === theme) return;
    if (typeof document.startViewTransition !== 'function' || prefersReducedMotion()) {
        applyAtOnce(theme);
        return;
    }
    crossfading = true;
    document.addEventListener('click', forwardToggleClick);
    const transition = document.startViewTransition(() => applyAtOnce(theme));
    // Rejects when the browser skips the animation (a hidden tab); the theme is applied anyway.
    transition.ready.catch(() => undefined);
    const done = () => {
        crossfading = false;
        document.removeEventListener('click', forwardToggleClick);
        switchToWanted();
    };
    transition.finished.then(done, done);
}

/**
 * Shows `theme` on the page. Clicks during a crossfade only record the latest choice, which
 * is shown when the crossfade ends, so quick toggling never stacks or cuts off fades.
 */
export function showTheme(theme: Theme): void {
    wanted = theme;
    if (!crossfading) switchToWanted();
}
